import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import { Modal, Skeleton, Tag } from "antd"
import { gen, swimminglyApi } from "./utils"

interface IRemovedMeetPreview {
  meet: {
    meetId: number
    meetName: string
    meetDate: string
    hostClubName: string | null
    removedAt?: string | null
    removedByNameSnapshot?: string | null
    removedByEmailSnapshot?: string | null
    removedByClubNameSnapshot?: string | null
  }
  impact?: {
    activeClubs?: Array<{
      clubId: number
      clubName: string
      entryCount: number
    }>
  }
}

export default function RemovedMeetModal({
  visible,
  setVisible,
  meetId,
}: {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  meetId: number | null
}): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<IRemovedMeetPreview | null>(null)

  useEffect(() => {
    if (!visible || !meetId) {
      if (!visible) {
        setPreview(null)
      }
      return
    }

    let mounted = true
    setLoading(true)

    swimminglyApi
      .get(gen(`/api/getMeetRemovalPreview/${meetId}`))
      .then((data: { status?: string } & IRemovedMeetPreview) => {
        if (mounted && data?.status === "success") {
          setPreview(data)
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [meetId, visible])

  return (
    <Modal
      title="Removed Meet"
      open={visible}
      footer={null}
      onCancel={() => setVisible(false)}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : preview?.meet ? (
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#101242" }}>
              {preview.meet.meetName}
            </div>
            <div style={{ marginTop: 6, color: "rgba(16, 18, 66, 0.68)" }}>
              {dayjs(preview.meet.meetDate).format("MM-DD-YYYY")}
              {preview.meet.hostClubName
                ? ` · Host: ${preview.meet.hostClubName}`
                : ""}
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {preview.meet.removedAt ? (
              <Tag color="default">
                Removed {dayjs(preview.meet.removedAt).format("MM-DD-YYYY h:mm A")}
              </Tag>
            ) : null}
            {preview.meet.removedByClubNameSnapshot ? (
              <Tag color="default">
                {preview.meet.removedByClubNameSnapshot}
              </Tag>
            ) : null}
          </div>

          <div style={{ display: "grid", gap: 8, color: "rgba(16, 18, 66, 0.82)" }}>
            <div>
              <strong>Removed by:</strong>{" "}
              {preview.meet.removedByNameSnapshot || "Unknown administrator"}
            </div>
            <div>
              <strong>Email:</strong>{" "}
              {preview.meet.removedByEmailSnapshot || "Unavailable"}
            </div>
            <div>
              <strong>Club affiliation:</strong>{" "}
              {preview.meet.removedByClubNameSnapshot || "Unavailable"}
            </div>
          </div>

          {Array.isArray(preview.impact?.activeClubs) &&
          preview.impact.activeClubs.length > 0 ? (
            <div style={{ display: "grid", gap: 8 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.24,
                  textTransform: "uppercase",
                  color: "rgba(16, 18, 66, 0.58)",
                }}
              >
                Clubs In The Meet At Removal
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {preview.impact.activeClubs.map((club) => (
                  <div
                    key={`removed-meet-club-${club.clubId}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "center",
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "rgba(16, 18, 66, 0.04)",
                      fontSize: 13,
                      color: "rgba(16, 18, 66, 0.82)",
                    }}
                  >
                    <span>{club.clubName}</span>
                    <span style={{ color: "rgba(16, 18, 66, 0.56)" }}>
                      {club.entryCount} entries
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(16, 18, 66, 0.04)",
              color: "rgba(16, 18, 66, 0.76)",
              lineHeight: 1.6,
            }}
          >
            Removed meets are no longer accessible in the Clubhouse and are not available to be sync'd with your Scorekeeper's iPad. Often, when a meet is removed, the host team has created a new one in its place. Please refer to your season's available meets.
          </div>
        </div>
      ) : null}
    </Modal>
  )
}